import type { FilterNode } from '@ofmos/shared/filter-ast';
import { encodeGroupKey, type RecordGroupTotals } from '@ofmos/shared/record-group';
import { type OrderBy, RECORD_QUERY_DEFAULT_LIMIT } from '@ofmos/shared/record-query';
import { useMemo } from 'react';
import type { FieldMetadata, ObjectMetadata, RecordGroupEntry } from '../../../data/client';
import { pagedIds, useInfiniteRecordList } from '../../../data/hooks';
import { TagDot } from '../../../fields/display/TagDot';
import { Button, cx, Icon, Skeleton } from '../../../ui';
import { MOTION } from '../../../ui/motion';
import type { GroupDisplay } from './groupDisplay';
import {
  aggregateLabel,
  aggregateLines,
  groupCountLabel,
  groupLabel,
  groupRowsFilter,
  groupTone,
} from './groupModel';
import { PlaceholderCard, RecordCard } from './RecordCard';
import { CARD_SECONDARY_FIELDS, type TableColumn } from './tableModel';
import { type GroupedTable, headerAggregate } from './useGroupedTable';
import { usePresentRows } from './usePresentRows';

/**
 * THE GROUPED LIST AT 375px (`docs/decisions/group-by-aggregation.md` D22).
 *
 * A section heading plus cards, and the footer becomes ONE LINE UNDER EACH SECTION rather
 * than a row: a table footer on a phone is a horizontal scroll for one number. A CURRENCY
 * aggregate is one line per code here too, which is the layout the phone was always going
 * to need and is the reason the per-code split costs the mobile view nothing extra.
 *
 * NO DRAG HERE, and its absence is a decision rather than a gap. D24's drop writes the
 * group field, and the phone's card list has never had an inline write of any kind - a card
 * opens the record page, which has room for real inputs, precisely because a 40px target
 * holding a gesture is a mis-tap waiting to happen. Changing a record's group on a phone is
 * one tap into the record and one field.
 */
export interface GroupedRecordCardListProps {
  readonly object: ObjectMetadata;
  readonly columns: readonly TableColumn[];
  readonly fields: readonly FieldMetadata[];
  readonly boardGroupFieldId: string | null;
  readonly grouped: GroupedTable;
  readonly display: GroupDisplay;
  readonly viewFilter: FilterNode | undefined;
  readonly orderBy: readonly OrderBy[] | undefined;
  readonly select: readonly string[];
  readonly archived: boolean;
}

const PLACEHOLDERS = ['1', '2', '3'] as const;

export function GroupedRecordCardList({
  object,
  columns,
  fields,
  boardGroupFieldId,
  grouped,
  display,
  viewFilter,
  orderBy,
  select,
  archived,
}: GroupedRecordCardListProps) {
  const secondary = columns.filter((column) => !column.isTitle).slice(0, CARD_SECONDARY_FIELDS);
  const groups = grouped.groups;

  if (groups === undefined) {
    return (
      <div aria-busy="true" className="space-y-3 pt-2">
        {PLACEHOLDERS.map((key) => (
          <Skeleton key={key} shape="block" className="h-24" />
        ))}
      </div>
    );
  }

  return (
    /**
     * `pb-20` CLEARS THE FLOATING BAR AT THE BOTTOM OF THE SCREEN. It was written for the
     * create circle, which wave 3 removed (PH-2); the tab bar it also cleared still floats
     * over the content, and a grouped list ENDS with the grand total - a number the reader
     * cannot scroll past, unlike the last card of a flat list. PH-1 replaces this with one
     * safe-area inset owned by the shell.
     */
    <div className="pb-20">
      {groups.map((group, index) => (
        <GroupCardSection
          key={encodeGroupKey(group.key)}
          object={object}
          secondary={secondary}
          columns={columns}
          fields={fields}
          boardGroupFieldId={boardGroupFieldId}
          grouped={grouped}
          group={group}
          index={index}
          display={display}
          viewFilter={viewFilter}
          orderBy={orderBy}
          select={select}
          archived={archived}
        />
      ))}

      {grouped.hasMoreGroups && (
        <p className="border-t border-line px-1 py-4 text-sm text-ink-muted">
          {`These are the first ${String(grouped.groupLimit)} groups, and there are more. Narrow the filter, or group by a field with fewer values, to see the rest.`}
        </p>
      )}

      {grouped.plan.footerIndex.size > 0 && (
        <div className="border-t-2 border-line-strong bg-surface-sunken px-1 py-3">
          <TotalLines grouped={grouped} totals={grouped.total} label="All groups" />
        </div>
      )}
    </div>
  );
}

interface GroupCardSectionProps {
  readonly object: ObjectMetadata;
  readonly secondary: readonly TableColumn[];
  /** Every visible column, for the card's checklist line (`phase-b-coach.md` D5). */
  readonly columns: readonly TableColumn[];
  readonly fields: readonly FieldMetadata[];
  readonly boardGroupFieldId: string | null;
  readonly grouped: GroupedTable;
  readonly group: RecordGroupEntry;
  readonly index: number;
  readonly display: GroupDisplay;
  readonly viewFilter: FilterNode | undefined;
  readonly orderBy: readonly OrderBy[] | undefined;
  readonly select: readonly string[];
  readonly archived: boolean;
}

function GroupCardSection({
  object,
  secondary,
  columns,
  fields,
  boardGroupFieldId,
  grouped,
  group,
  index,
  display,
  viewFilter,
  orderBy,
  select,
  archived,
}: GroupCardSectionProps) {
  const encoded = encodeGroupKey(group.key);
  const open = display.isOpen(encoded, index);
  const expanded = display.isExpanded(encoded);
  const label = groupLabel(group.key, grouped.resolved, group.labels);
  const tone = groupTone(group.key, grouped.resolved);

  const header = headerAggregate(grouped, group);
  const preview = usePresentRows(
    group.records.map((row) => row.id),
    open && !expanded,
  );

  return (
    <section>
      {/*
        A TINTED BAND, the same fill the desktop group heading carries. On a phone a section
        is fifteen cards long, so the boundary between two groups has to survive a scroll -
        a heading that is only a line of text disappears into the list it is dividing. It is
        the CONTENT layer, so it is opaque and hairlined, never glass (DESIGN.md).
      */}
      <header className="border-y border-line bg-surface-sunken">
        {/* The whole heading is the control, at the touch floor, with the numbers inside
            it: they are what the reader is deciding about when they open the section. */}
        <button
          type="button"
          aria-expanded={open}
          onClick={() => display.toggleOpen(encoded, index)}
          className={cx(
            'pressable flex min-h-(--touch-target) w-full items-center gap-2 px-1 py-2 text-left',
            'focus-visible:outline-2 focus-visible:outline-accent',
          )}
        >
          <Icon
            name="chevron-right"
            size="0.9rem"
            className={cx('shrink-0 text-ink-subtle', MOTION.turn, open && 'rotate-90')}
          />
          {tone === null ? (
            <span className="truncate text-base font-semibold text-ink">{label}</span>
          ) : (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <TagDot tone={tone} />
              <span className="truncate text-ink">{label}</span>
            </span>
          )}
          <span className="ml-auto shrink-0 text-xs tabular-nums text-ink-muted">
            {groupCountLabel(group.count)}
          </span>
        </button>

        {header !== null && (
          <p className="px-1 pb-2 text-xs text-ink-muted">
            {`${header.label}: `}
            <span className="tabular-nums text-ink">{header.lines.join(' · ')}</span>
          </p>
        )}
      </header>

      {open &&
        (expanded ? (
          <ExpandedGroupCards
            object={object}
            secondary={secondary}
            columns={columns}
            fields={fields}
            boardGroupFieldId={boardGroupFieldId}
            grouped={grouped}
            group={group}
            viewFilter={viewFilter}
            orderBy={orderBy}
            select={select}
            archived={archived}
          />
        ) : (
          <>
            <ul className="divide-y divide-line-subtle border-y border-line">
              {preview.ids.map((recordId) => (
                <RecordCard
                  key={recordId}
                  object={object}
                  secondary={secondary}
                  fields={fields}
                  boardGroupFieldId={boardGroupFieldId}
                  columns={columns}
                  recordId={recordId}
                  present={preview.present(recordId)}
                  onExited={() => preview.exited(recordId)}
                  archived={archived}
                />
              ))}
            </ul>
            {group.records.length === 0 && (
              <p className="px-1 pb-3 text-sm text-ink-subtle">No records in this group.</p>
            )}
            {group.count > group.records.length && (
              <div className="px-1 py-2">
                <Button variant="ghost" size="sm" onClick={() => display.expand(encoded)}>
                  Show all
                </Button>
              </div>
            )}
          </>
        ))}

      {open && grouped.plan.footerIndex.size > 0 && (
        <div className="px-1 py-3">
          <TotalLines grouped={grouped} totals={group} label="Group total" />
        </div>
      )}
    </section>
  );
}

function ExpandedGroupCards({
  object,
  secondary,
  columns,
  fields,
  boardGroupFieldId,
  grouped,
  group,
  viewFilter,
  orderBy,
  select,
  archived,
}: Omit<GroupCardSectionProps, 'index' | 'display'>) {
  const filter = useMemo(
    () => groupRowsFilter(group.key, grouped.resolved, viewFilter),
    [group.key, grouped.resolved, viewFilter],
  );

  const list = useInfiniteRecordList(object.id, {
    select,
    filter,
    ...(orderBy === undefined ? {} : { orderBy }),
    // The same page as the desktop group, at the same one constant (D2).
    limit: RECORD_QUERY_DEFAULT_LIMIT,
    archivedOnly: archived,
  });

  const ids = pagedIds(list.data) ?? [];
  const rows = usePresentRows(ids);

  if (list.isError) {
    return (
      <p role="alert" className="px-1 py-3 text-sm text-danger">
        {`These records could not be loaded. ${list.error.message}`}
      </p>
    );
  }

  return (
    <>
      <ul className="divide-y divide-line-subtle border-y border-line">
        {list.isPending
          ? PLACEHOLDERS.map((key) => <PlaceholderCard key={key} fieldCount={secondary.length} />)
          : rows.ids.map((recordId) => (
              <RecordCard
                key={recordId}
                object={object}
                secondary={secondary}
                fields={fields}
                boardGroupFieldId={boardGroupFieldId}
                columns={columns}
                recordId={recordId}
                present={rows.present(recordId)}
                onExited={() => rows.exited(recordId)}
                archived={archived}
              />
            ))}
      </ul>
      {list.hasNextPage === true && (
        <div className="px-1 py-2">
          <Button
            variant="ghost"
            size="sm"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Show more
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * The column totals as LINES, named. A footer row's column position is what says which
 * number belongs to which field, and a phone has no column position to lean on - so the
 * field's own label carries it instead.
 */
function TotalLines({
  grouped,
  totals,
  label,
}: {
  readonly grouped: GroupedTable;
  readonly totals: RecordGroupTotals | undefined;
  readonly label: string;
}) {
  return (
    <dl className="space-y-1">
      <dt className="text-2xs font-semibold tracking-widest text-ink-muted uppercase">{label}</dt>
      {[...grouped.plan.footerIndex].map(([fieldId, at]) => {
        const field = grouped.fieldById.get(fieldId) ?? null;
        const op = grouped.plan.requests[at]?.op ?? 'count';
        return (
          <dd key={fieldId} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-ink-muted">
              {`${aggregateLabel(op, field?.type ?? null)} of ${field?.label ?? 'a field'}`}
            </span>
            <span className="flex shrink-0 flex-col items-end tabular-nums text-ink">
              {/* Nothing at all while the read is in flight: an em dash is an ANSWER, and
                  it means "no rows, or no non-null values" (D12). */}
              {totals === undefined
                ? null
                : aggregateLines(totals.aggregates[at], op, field).map((line) => (
                    <span key={line}>{line}</span>
                  ))}
            </span>
          </dd>
        );
      })}
    </dl>
  );
}
